import React, { useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faWindowClose, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getZones } from '../../../../redux/slices/lists/zonesSlice';
import { getAuthorizedZones } from '../../../../redux/slices/lists/zonesSlice';
import { fetchConfigs } from '../../../../redux/slices/configSlice';
import { postSchedulingWindow, putSchedulingWindow, evaluateSchedulingWindow, clearEvaluation } from '../../../../redux/slices/scheduling_windowsSlice';

import BasicInfo from './BasicInfo';
import SchedulesSection from './SchedulesSection/SchedulesSection';
import LinksSection from './LinksSection/LinksSection';
import { deepClean, msFrom } from './helpers';

import { SchedulingWindowModalProps, ScheduleUI } from './types';
import OpeningMomentsModal from './OpeningMomentsModal';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const exactlyOnePicked = <T,>(a?: T, b?: T) => (a != null && b == null) || (a == null && b != null);
const lte = (a?: number, b?: number) => a == null || b == null || a <= b;

const isScheduleValid = (s: ScheduleUI) => {
  if (s.start_time && s.end_time && s.start_time === s.end_time) return false;

  if (exactlyOnePicked(s.date_from, s.date_until)) return false;
  if (s.date_from && s.date_until && s.date_from > s.date_until) return false;

  if (exactlyOnePicked(s.month_from, s.month_to)) return false;
  if (!lte(s.month_from, s.month_to)) return false;

  if (exactlyOnePicked(s.wom_from, s.wom_to)) return false;
  if (!lte(s.wom_from, s.wom_to)) return false;

  if (exactlyOnePicked(s.dow_from, s.dow_to)) return false;
  if (!lte(s.dow_from, s.dow_to)) return false;

  if (exactlyOnePicked(s.nth_of_month_from, s.nth_of_month_until)) return false;
  if (!lte(s.nth_of_month_from, s.nth_of_month_until)) return false;

  return true;
};

const getSchedulingWindowPath = (window: any): string | null => {
  if (!window?.zone_name || !window?.name) return null;
  return `${window.zone_name}|${window.name}`;
};

const collectSchedulingWindowDependencyPaths = (window: any): string[] => {
  const paths = new Set<string>();
  const addPath = (path: unknown) => {
    if (typeof path !== 'string') return;
    const trimmed = path.trim();
    if (trimmed) {
      paths.add(trimmed);
    }
  };

  for (const ref of (window?.references ?? []) as any[]) {
    addPath(ref?.scheduling_window_path);
  }

  for (const schedule of (window?.schedules ?? []) as any[]) {
    addPath(schedule?.shift_if_closed_window_path);
    addPath(schedule?.nth_of_month_from_in_scheduling_window_path);
    addPath(schedule?.nth_of_month_until_in_scheduling_window_path);
    addPath(schedule?.open_during_scheduling_window_path);
    addPath(schedule?.closed_during_scheduling_window_path);
  }

  return Array.from(paths);
};

const canReachWindowId = (startWindowId: number, targetWindowId: number, dependencyGraph: Map<number, Set<number>>): boolean => {
  const stack = [startWindowId];
  const visited = new Set<number>();

  while (stack.length > 0) {
    const windowId = stack.pop();
    if (windowId == null || visited.has(windowId)) continue;
    if (windowId === targetWindowId) return true;

    visited.add(windowId);
    const nextWindowIds = dependencyGraph.get(windowId);
    if (!nextWindowIds) continue;

    for (const nextWindowId of nextWindowIds) {
      if (!visited.has(nextWindowId)) {
        stack.push(nextWindowId);
      }
    }
  }

  return false;
};

const SchedulingWindowModal: React.FC<SchedulingWindowModalProps> = ({ show, handleClose, mode, data, createIn }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const zones = useAppSelector((state) => state.listOfZones?.zones || []);
  const schedulingWindows = useAppSelector((state) => state.schedulingWindow?.items || []);

  const currentWindowId = mode === 'edit' ? data?.scheduling_window_id : undefined;
  const dependencyGraph = useMemo((): Map<number, Set<number>> => {
    const pathToId = new Map<string, number>();
    const graph = new Map<number, Set<number>>();

    for (const window of schedulingWindows) {
      const windowId = Number(window?.scheduling_window_id);
      if (!Number.isFinite(windowId)) continue;

      graph.set(windowId, new Set<number>());

      const path = getSchedulingWindowPath(window);
      if (path) {
        pathToId.set(path, windowId);
      }
    }

    for (const window of schedulingWindows) {
      const windowId = Number(window?.scheduling_window_id);
      if (!Number.isFinite(windowId)) continue;

      const targetWindowIds = graph.get(windowId);
      if (!targetWindowIds) continue;

      for (const dependencyPath of collectSchedulingWindowDependencyPaths(window)) {
        const dependencyWindowId = pathToId.get(dependencyPath);
        if (dependencyWindowId != null) {
          targetWindowIds.add(dependencyWindowId);
        }
      }
    }

    return graph;
  }, [schedulingWindows]);

  const circularWindowIds = useMemo((): Set<number> => {
    const set = new Set<number>();
    if (!currentWindowId) return set;

    for (const window of schedulingWindows) {
      const windowId = Number(window?.scheduling_window_id);
      if (!Number.isFinite(windowId)) continue;

      if (canReachWindowId(windowId, currentWindowId, dependencyGraph)) {
        set.add(windowId);
      }
    }

    return set;
  }, [currentWindowId, dependencyGraph, schedulingWindows]);

  const forbiddenLinkWindowIds = useMemo((): Set<number> => {
    const set = new Set<number>(circularWindowIds);
    if (currentWindowId) {
      set.delete(currentWindowId);
    }
    return set;
  }, [circularWindowIds, currentWindowId]);

  const forbiddenScheduleDependencyWindowIds = useMemo((): Set<number> => {
    const set = new Set<number>(circularWindowIds);
    if (currentWindowId) {
      set.add(currentWindowId);
    }
    return set;
  }, [circularWindowIds, currentWindowId]);

  const configItems = useAppSelector((state) => state.config?.items || []);
  const evaluatedPeriods = useAppSelector((state) => state.schedulingWindow?.evaluation?.periods || []);
  const isEvaluating = useAppSelector((state) => state.schedulingWindow?.loading?.evaluateSchedulingWindow || false);
  const evaluateError = useAppSelector((state) => state.schedulingWindow?.evaluation?.error || null);

  const getDefaultTimezone = (): string => {
    // TODO #757 step 3 PR D: Config.value is `object` in the slice; tighten
    // to a discriminated union by `key` so this access doesn't need a cast.
    const configValue = configItems.find((c: any) => c.key === 'default_timezone')?.value as
      | string
      | { timezone?: string; default_timezone?: string; value?: string }
      | undefined;

    const timezoneFromConfig = typeof configValue === 'string' ? configValue : configValue?.timezone || configValue?.default_timezone || configValue?.value;

    const normalizedTimezone = typeof timezoneFromConfig === 'string' ? timezoneFromConfig.trim() : '';

    return normalizedTimezone || getBrowserTimezone() || 'UTC';
  };

  // basic info
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('UTC'); // IANA
  const [description, setDescription] = useState('');
  const [zone, setZone] = useState('');
  const [folderId, setFolderId] = useState<number | null>(null);
  const [isCalendar, setIsCalendar] = useState(false);
  const [dayRollover, setDayRollover] = useState<string | ''>('');
  const [minOpenValue, setMinOpenValue] = useState<number | ''>('');
  const [minOpenUnit, setMinOpenUnit] = useState<'minutes' | 'hours'>('minutes');

  const [schedules, setSchedules] = useState<ScheduleUI[]>([]);
  const [links, setLinks] = useState<
    Array<{
      link_type: 'open' | 'closed';
      zone_name: string;
      window_name: string;
      offset_type: 'before' | 'after' | 'on';
      offset_days: number;
    }>
  >([]);

  const hasScheduleErrors = useMemo(() => schedules.some((s) => !isScheduleValid(s)), [schedules]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const currentFormValues = useMemo(
    () => ({
      name,
      timezone,
      description,
      zone,
      isCalendar,
      dayRollover,
      minOpenValue,
      minOpenUnit,
      schedules,
      links,
    }),
    [name, timezone, description, zone, isCalendar, dayRollover, minOpenValue, minOpenUnit, schedules, links]
  );
  const isDirtyRaw = useUnsavedChanges(show, currentFormValues);
  const isDirty = mode === 'edit' && isDirtyRaw;

  // Opening Moments Modal state
  const [showOpeningMomentsModal, setShowOpeningMomentsModal] = useState(false);

  const handleEvaluateWindow = () => {
    if (mode !== 'edit' || !data?.zone_name || !data?.name) return;

    setShowOpeningMomentsModal(true);
    dispatch(
      evaluateSchedulingWindow({
        zone_name: data.zone_name,
        window_name: data.name,
        timezone,
      })
    );
  };

  useEffect(() => {
    const action = mode === 'edit' && data ? 'update' : 'create';
    dispatch(getAuthorizedZones({ resourceType: 'scheduling_windows', action }) as any);
    dispatch(fetchConfigs());
  }, [dispatch, data, mode]);

  const refToString = (val: any): string | undefined => {
    if (!val) return undefined;
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val.zone_name && val.window_name) {
      return `${val.zone_name}|${val.window_name}`;
    }
    if (!Number.isNaN(Number(val))) return String(val);
    return undefined;
  };

  const apiScheduleToUi = (s: any, windowTimezone: string): ScheduleUI => ({
    scheduleName: s.name || s.scheduleName || '',
    description: s.description || undefined,

    start_time: s.start_time ?? s.time_from ?? undefined,
    end_time: s.end_time ?? s.time_to ?? undefined,

    date_from: s.date_from ?? undefined,
    date_until: s.date_until ?? undefined,

    month_from: s.month_from ?? s.month ?? undefined,
    month_to: s.month_until ?? s.month ?? undefined,

    wom_from: s.week_of_month_from ?? s.week_of_month ?? undefined,
    wom_to: s.week_of_month_until ?? s.week_of_month ?? undefined,

    dow_from: s.day_of_week_from ?? s.day_of_week ?? undefined,
    dow_to: s.day_of_week_until ?? s.day_of_week ?? undefined,

    nth_of_month_from: s.nth_of_month_from ?? undefined,
    nth_of_month_until: s.nth_of_month_until ?? undefined,

    shift_if_closed_window_path: s.shift_if_closed_window_path,
    shift_open_days: typeof s.shift_open_days === 'number' ? s.shift_open_days : undefined,
    nth_of_month_from_in_scheduling_window_path: s.nth_of_month_from_in_scheduling_window_path,
    nth_of_month_until_in_scheduling_window_path: s.nth_of_month_until_in_scheduling_window_path,
    open_during_scheduling_window_path: s.open_during_scheduling_window_path,
    closed_during_scheduling_window_path: s.closed_during_scheduling_window_path,

    timezone: s.timezone || windowTimezone || 'UTC',
  });

  const uiScheduleToApi = (s: ScheduleUI, winTimezone: string) => {
    const obj: any = {
      ...(s.scheduleName && { name: s.scheduleName }),
      ...(s.description && { description: s.description }),
      ...(s.start_time && { start_time: s.start_time }),
      ...(s.end_time && { end_time: s.end_time }),
      ...(s.date_from && { date_from: s.date_from }),
      ...(s.date_until && { date_until: s.date_until }),

      ...(s.month_from != null && { month_from: s.month_from }),
      ...(s.month_to != null && { month_until: s.month_to }),

      ...(s.wom_from != null && { week_of_month_from: s.wom_from }),
      ...(s.wom_to != null && { week_of_month_until: s.wom_to }),

      ...(s.dow_from != null && { day_of_week_from: s.dow_from }),
      ...(s.dow_to != null && { day_of_week_until: s.dow_to }),

      ...(s.nth_of_month_from != null && {
        nth_of_month_from: s.nth_of_month_from,
      }),
      ...(s.nth_of_month_until != null && {
        nth_of_month_until: s.nth_of_month_until,
      }),

      ...(s.shift_if_closed_window_path && {
        shift_if_closed_window_path: s.shift_if_closed_window_path,
      }),
      ...(typeof s.shift_open_days === 'number' && {
        shift_open_days: s.shift_open_days,
      }),
      ...(s.nth_of_month_from_in_scheduling_window_path && {
        nth_of_month_from_in_scheduling_window_path: s.nth_of_month_from_in_scheduling_window_path,
      }),
      ...(s.nth_of_month_until_in_scheduling_window_path && {
        nth_of_month_until_in_scheduling_window_path: s.nth_of_month_until_in_scheduling_window_path,
      }),
      ...(s.open_during_scheduling_window_path && {
        open_during_scheduling_window_path: s.open_during_scheduling_window_path,
      }),
      ...(s.closed_during_scheduling_window_path && {
        closed_during_scheduling_window_path: s.closed_during_scheduling_window_path,
      }),

      timezone: s.timezone || winTimezone || 'UTC',
    };

    return obj;
  };

  const apiRefsToUiLinks = (refs: any[], zonesList: any[]) => {
    return (refs || []).map((r) => {
      let zoneName = r.zone_name;
      let windowName = r.window_name;

      // Parse scheduling_window_path: "Zone|WindowName"
      if (r.scheduling_window_path && typeof r.scheduling_window_path === 'string') {
        const parts = r.scheduling_window_path.split('|');
        if (parts.length === 2) {
          zoneName = parts[0];
          windowName = parts[1];
        }
      }

      const z = zonesList.find((zone: any) => zone.name === zoneName);
      return {
        link_type: (r.type === 'closed' ? 'closed' : 'open') as 'open' | 'closed',
        zone_name: z ? z.zone_id : zoneName,
        window_name: windowName,
        offset_type: 'on' as const,
        offset_days: 0,
      };
    });
  };

  const uiLinksToApiRefs = (rows: any[], zonesList: any[]) => {
    return rows
      .filter((r) => r.window_name)
      .map((r) => {
        const zoneObj = zonesList.find((z: any) => String(z.zone_id) === String(r.zone_name));
        const zoneName = zoneObj ? zoneObj.name : r.zone_name;

        return {
          scheduling_window_path: `${zoneName}|${r.window_name}`,
          type: r.link_type,
        };
      });
  };

  useEffect(() => {
    if ((mode === 'edit' || mode === 'duplicate') && data) {
      setName(mode === 'duplicate' ? '' : data.name || '');
      setTimezone(data.timezone || 'UTC');
      setDescription(data.description || '');
      setIsCalendar(Boolean((data as any).is_calendar));
      setDayRollover((data as any).day_rollover || '');
      setMinOpenValue((data as any).min_open_duration_ms != null ? Math.round(Number((data as any).min_open_duration_ms) / 60000) : '');
      setMinOpenUnit('minutes');

      // schedules (normalize API -> UI)
      const mappedSchedules = (data.schedules || []).map((s: any) => apiScheduleToUi(s, data.timezone));

      setSchedules(mappedSchedules.length ? mappedSchedules : []);

      const matchedZone = zones.find((z: any) => z.name === data.zone_name);
      setZone(matchedZone ? String(matchedZone.zone_id) : '');
      setFolderId(data.folder_id ?? null);

      const refRows = apiRefsToUiLinks((data as any).references || [], zones);
      setLinks(refRows);
    } else if (mode === 'create') {
      setName('');
      setTimezone(getDefaultTimezone());
      setDescription('');
      setIsCalendar(false);
      setDayRollover('');
      setMinOpenValue('');
      setMinOpenUnit('minutes');
      setSchedules([]);
      setLinks([]);
      setZone(createIn ? String(createIn.zoneId) : '');
      setFolderId(createIn?.folderId ?? null);
    }
  }, [mode, data, zones, configItems, createIn]);

  const submissionData = useMemo(() => {
    const rawSchedules = schedules.map((s) => uiScheduleToApi(s, timezone)).filter((obj) => Object.keys(obj).length > 0);

    const apiReferences = uiLinksToApiRefs(links, zones);

    const zoneObject = zones.find((z: any) => String(z.zone_id) === String(zone));
    const minOpenMs = typeof minOpenValue === 'number' ? msFrom(minOpenValue, minOpenUnit) : undefined;

    return {
      ...deepClean({
        name,
        timezone,
        zone_name: zoneObject?.name || '',
        is_calendar: isCalendar || undefined,
        day_rollover: dayRollover || undefined,
        min_open_duration_ms: minOpenMs ?? undefined,
        schedules: rawSchedules,
        references: apiReferences,
      }),
      description,
      // Outside deepClean so a cleared folder (null) is still sent.
      folder_id: folderId,
    };
  }, [name, timezone, description, zone, folderId, schedules, links, zones, isCalendar, dayRollover, minOpenValue, minOpenUnit]);

  const onSubmit = async () => {
    if (hasScheduleErrors) return;
    setIsSubmitting(true);
    try {
      if (mode === 'create' || mode === 'duplicate') {
        const result = await dispatch(postSchedulingWindow(submissionData));
        if (postSchedulingWindow.rejected.match(result)) return;
      } else if (mode === 'edit' && data?.scheduling_window_id) {
        const result = await dispatch(
          putSchedulingWindow({
            zone_name: data.zone_name,
            window_name: data.name,
            data: submissionData,
          })
        );
        if (putSchedulingWindow.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setName('');
    setTimezone(getDefaultTimezone());
    setDescription('');
    setZone('');
    setFolderId(null);
    setIsCalendar(false);
    setDayRollover('');
    setMinOpenValue('');
    setMinOpenUnit('minutes');
    setSchedules([]);
    setLinks([]);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  return (
    <Modal show={show} onHide={handleRequestClose} backdrop="static" size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === 'edit' ? t('Edit Scheduling Window') : mode === 'duplicate' ? t('Duplicate Scheduling Window') : t('New Scheduling Window')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <BasicInfo
          name={name}
          setName={setName}
          timezone={timezone}
          setTimezone={setTimezone}
          description={description}
          setDescription={setDescription}
          zone={zone}
          setZone={(v) => {
            // Reset the (zone-scoped) folder when the zone changes.
            setZone(v);
            setFolderId(null);
          }}
          folderId={folderId}
          setFolderId={setFolderId}
          zones={zones}
          isCalendar={isCalendar}
          setIsCalendar={setIsCalendar}
          dayRollover={dayRollover}
          setDayRollover={setDayRollover}
          minOpenValue={minOpenValue}
          setMinOpenValue={setMinOpenValue}
          minOpenUnit={minOpenUnit}
          setMinOpenUnit={setMinOpenUnit}
        />

        <SchedulesSection
          schedules={schedules}
          setSchedules={setSchedules}
          schedulingWindows={schedulingWindows}
          zones={zones}
          windowTimezone={timezone}
          forbiddenScheduleDependencyWindowIds={forbiddenScheduleDependencyWindowIds}
        />

        <LinksSection links={links} setLinks={setLinks} zones={zones} schedulingWindows={schedulingWindows} forbiddenLinkWindowIds={forbiddenLinkWindowIds} />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose}>
          <FontAwesomeIcon icon={faWindowClose} className="me-2" />
          Close
        </Button>
        <Button variant="outline-info" disabled={mode !== 'edit' || isEvaluating || !data?.zone_name} onClick={handleEvaluateWindow}>
          <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
          {isEvaluating ? t('Evaluating...') : t('Preview Open Periods')}
        </Button>
        <Button variant="primary" disabled={isSubmitting || hasScheduleErrors} onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Save and Close
        </Button>
      </Modal.Footer>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />

      {showOpeningMomentsModal && (
        <OpeningMomentsModal
          show={showOpeningMomentsModal}
          onHide={() => {
            setShowOpeningMomentsModal(false);
            dispatch(clearEvaluation());
          }}
          periods={evaluatedPeriods}
          isLoading={isEvaluating}
          error={evaluateError}
          windowName={data?.name || ''}
          timezone={timezone}
        />
      )}
    </Modal>
  );
};

export default SchedulingWindowModal;
